import { LocationModel } from "./LocationModel";
import bikeAvailableIcon from "../assets/bikeAvailableIcon.png";
import bikeRentedIcon from "../assets/bikeRentedIcon.png";

export const BikeTechnicalStatus = {
  functional: { value: "functional", name: "Sprawny", color: "#2e7d32" },
  damaged: { value: "damaged", name: "Uszkodzony", color: "#ed6c02" },
  missing: { value: "missing", name: "Zaginiony", color: "#d32f2f" },
};

export const BikeLogisticsStatus = Object.freeze({
  none: { value: "none", name: "Brak statusu logistycznego" },
  transportRelocation: {
    value: "transportRelocation",
    name: "Transport/relokacja",
  },
  warehouse: { value: "warehouse", name: "Magazyn" },
});

const normalizeLogisticsStatus = (logisticsStatus) => {
  if (typeof logisticsStatus === "number") {
    if (logisticsStatus === 1) return BikeLogisticsStatus.transportRelocation.value;
    if (logisticsStatus === 2) return BikeLogisticsStatus.warehouse.value;
    return BikeLogisticsStatus.none.value;
  }

  if (logisticsStatus) {
    const status = logisticsStatus.charAt(0).toLowerCase() + logisticsStatus.slice(1);
    if (BikeLogisticsStatus[status]) return status;
  }

  return BikeLogisticsStatus.none.value;
};

const normalizeTechnicalStatus = (technicalStatus, isMissing) => {
  if (typeof technicalStatus === "number") {
    if (technicalStatus === 2) return BikeTechnicalStatus.missing.value;
    if (technicalStatus === 1) return BikeTechnicalStatus.damaged.value;
    return BikeTechnicalStatus.functional.value;
  }

  if (technicalStatus) {
    const status = technicalStatus.charAt(0).toLowerCase() + technicalStatus.slice(1);
    if (BikeTechnicalStatus[status]) return status;
  }

  if (isMissing) return BikeTechnicalStatus.missing.value;
  return BikeTechnicalStatus.functional.value;
};

export const BikeHistoryItemType = {
  serviceConfirmed: {
    value: "serviceConfirmed",
    name: "Potwierdzenie serwisu",
  },
  bikeOutOfService: { value: "bikeOutOfService", name: "Wyłączenie z użytku" },
  bikeInService: { value: "bikeInService", name: "Przywrócenie do użytku" },
  bikeMarkedAsMissing: {
    value: "bikeMarkedAsMissing",
    name: "Oznaczenie jako zaginiony",
  },
  bikeMarkedAsFound: {
    value: "bikeMarkedAsFound",
    name: "Oznaczenie jako odnaleziony",
  },
  defectResolved: { value: "defectResolved", name: "Zamknięcie usterki" },
  defectRejected: { value: "defectRejected", name: "Odrzucenie usterki" },
  defectResolvedWithParts: {
    value: "defectResolvedWithParts",
    name: "Zamknięcie usterki z wymianą",
  },
  bikeNoteAdded: { value: "bikeNoteAdded", name: "Dodanie notatki" },
  bikeLogisticsStatusChanged: {
    value: "bikeLogisticsStatusChanged",
    name: "Zmiana statusu logistycznego",
  },
};

export class BikeModel {
  constructor(data) {
    if (data) {
      validate(data.id, "id");
      validate(data.bikeNumber, "bikeNumber");
    }
    data = data || {};
    this.id = data.id;
    this.bikeId = data.bikeId;
    this.bikeNumber = data.bikeNumber;
    this.bikeSerialNumber = data.bikeSerialNumber;
    this.location = data.location
      ? new LocationModel(data.location.lat, data.location.lng)
      : null;
    this.lockImei = data.lockImei;
    this.lockBatteryLevel = data.lockBatteryLevel;
    this.bikeBatteryLevel =
      data.bikeBatteryLevel != null &&
      data.bikeBatteryLevel >= 0 &&
      data.bikeBatteryLevel <= 100
        ? data.bikeBatteryLevel
        : null;
    this.insideOperationsZone = data.insideOperationsZone;
    this.lockTimestamp = data.lockTimestamp;
    this.gpsTimestamp = data.gpsTimestamp;
    this.satellites = data.satellites;
    this.isAvailableForRental = data.isAvailableForRental;
    this.isRentalRestricted = data.isRentalRestricted;
    this.rentalRestrictionReason = data.rentalRestrictionReason;
    this.isReserved = data.isReserved;
    this.isRented = data.isRented;
    this.hasAlarmEnabled = data.hasAlarmEnabled;
    this.isOrientedUpright = data.isOrientedUpright;
    this.bikeIcon = data.icon
      ? data.icon
      : this.isAvailableForRental
        ? bikeAvailableIcon
        : bikeRentedIcon;
    this.isSponsored = data.isSponsored || false;
    this.internalUseOnly = data.internalUseOnly;
    this.ongoingPause = data.ongoingPause;
    this.ongoingPauseDate = data.ongoingPauseDate;
    this.lastRideId = data.lastRideId;
    this.lockPhoneNumber = data.lockPhoneNumber;
    const lockState = data.lockState
      ? data.lockState === "closed"
        ? "0"
        : "1"
      : "-";
    const shackleState = data.shackleState
      ? data.shackleState === "closed"
        ? "0"
        : "1"
      : "-";
    this.lockShackleState = `${lockState}${shackleState}`;
    this.openDefectsCount = data.openDefectsCount || 0;
    this.technicalStatus = normalizeTechnicalStatus(
      data.technicalStatus,
      data.isMissing
    );
    this.isMissing = this.technicalStatus === BikeTechnicalStatus.missing.value;
    this.logisticsStatus = normalizeLogisticsStatus(data.logisticsStatus);
    this.alerts = data.alerts || [];
    this.lastServiceTime = data.lastServiceTime || null;
    this.nextServiceTime = data.nextServiceTime;
    this.serviceDue = data.serviceDue;
    this.lockDeviceType = data.lockDeviceType;
    this.bikeTypeName = data.typeName;
    this.enabledEngine = data.enabledEngine;
  }

  static getBikeTechnicalStatusDescription(bike) {
    const technicalStatus = BikeTechnicalStatus[bike.technicalStatus] ||
      BikeTechnicalStatus.functional;
    return technicalStatus.name.toLowerCase();
  }

  static getBikeStatusDescription(bike) {
    const statuses = [];
    if (bike.isAvailableForRental) statuses.push("dostępny");
    if (bike.isRented) statuses.push("wypożyczony");
    if (bike.ongoingPause) statuses.push("pauza");
    if (bike.isReserved) statuses.push("zarezerwowany");
    if (bike.isRentalRestricted)
      statuses.push("chwilowo wycofany z użytkowania");
    return statuses.join(", ");
  }

  static getBikeLogisticsStatusDescription(bike) {
    const status = BikeLogisticsStatus[bike.logisticsStatus] ||
      BikeLogisticsStatus.none;
    return status.name;
  }
}

const validate = (value, name, model = "BikeModel", isInvalid = (v) => !v) => {
  if (isInvalid(value)) {
    /*eslint no-console:0*/
    console.error(`${model} requires ${name}`);
  }
};
